// ─── Intent Detector — client-side keyword scorer for chatbot routing ─────────
// Routes user messages to 'agent' (live operational data via tool_use) or
// 'kb' (knowledge base RAG documentation). No ML model — pure keyword scoring.
//
// Session stickiness: once agent mode activates, follow-ups stay in agent mode.
// Reset to auto-detect on new chat session.
// ─────────────────────────────────────────────────────────────────────────────

// Multi-word phrases score higher (weight 3) to reduce false positives.
// Single keywords score 1.
const AGENT_PHRASES = [
  // Alerts
  'active alert', 'open alert', 'critical alert', 'alert count',
  'how many alerts', 'which alerts', 'unresolved alert',
  // Staffing
  'call out', 'call-out', 'float pool', 'staff ratio', 'open shift',
  'coverage rate', 'coverage gap', 'staffing coverage', 'daily staffing',
  'absence rate', 'absence pattern', 'workload strain',
  // Episodes / Cases
  'stalled episode', 'active episode', 'orphaned task',
  // Credentials
  'expiring credential', 'credential compliance', 'BLS expiring',
  'DEA expiring', 'license expiring',
  // Quality
  'medication error', 'safety incident', 'quality incident',
  'quality score', 'incident count', 'HEDIS measure', 'care gap',
  'regulatory deadline',
  // Finance
  'labor cost', 'cost per visit', 'AR aging', 'denied claim',
  'budget variance', 'variance to budget', 'revenue per patient',
  // Access
  'wait time', 'no-show', 'no show', 'open slot', 'same-day access',
  'prior auth', 'prior authorization',
  // Consumer
  'patient satisfaction', 'Google rating', 'open complaint',
  // Population Health
  'care gap', 'diabetes registry', 'CHF registry', 'COPD registry',
  'outreach backlog',
  // Practice Management
  'completion rate', 'overdue task', 'skip rate',
  // General operational
  "what's the", 'how many', 'how much', 'show me the', 'give me the',
  'tell me about the', 'what are the', 'are there any',
  'this week', 'right now', 'currently',
];

const AGENT_WORDS = [
  // Alerts
  'alert', 'alerts', 'severity', 'acknowledge', 'escalation', 'escalate',
  // Staffing
  'staffing', 'coverage', 'vacancy', 'vacancies', 'callout', 'callouts',
  'absence', 'absences', 'overtime', 'OT', 'strain', 'FTE', 'ratio',
  'shift', 'shifts', 'schedule', 'scheduling',
  // Episodes
  'episode', 'episodes', 'stalled', 'handoff', 'orphan', 'orphaned',
  // Credentials
  'credential', 'credentials', 'BLS', 'DEA', 'license', 'licenses',
  'certification', 'certifications', 'expiring', 'expired', 'renewal',
  'compliance',
  // Quality
  'incident', 'incidents', 'HEDIS', 'accreditation', 'regulatory',
  'safety', 'quality',
  // Finance
  'budget', 'revenue', 'variance', 'collections', 'claims', 'billing',
  'financial',
  // Access
  'backlog', 'utilization', 'referral', 'referrals', 'capacity',
  // Consumer
  'complaint', 'complaints', 'CAHPS', 'CPFI', 'NPS', 'survey', 'surveys',
  'reputation', 'rating', 'ratings', 'review', 'reviews',
  // Population Health
  'registry', 'registries', 'diabetes', 'CHF', 'COPD', 'panel',
  'outreach', 'population',
  // Practice Management
  'overdue', 'initiative', 'initiatives',
  // General operational
  'active', 'open', 'trending', 'dropped', 'increased', 'declined',
  'breach', 'breached', 'threshold', 'impact', 'cascade', 'KPI', 'KPIs',
  'metric', 'metrics', 'data', 'count', 'total', 'score',
];

const KB_PHRASES = [
  'how do I', 'how to', 'where is', 'what does', 'what is the purpose',
  'show me how', 'help me', 'walk me through', 'step by step',
  'tell me about the feature', 'tell me about the page',
  'how does the page work', 'explain the',
];

const KB_WORDS = [
  'tutorial', 'guide', 'documentation', 'configure', 'setting', 'settings',
  'navigation', 'navigate', 'menu', 'sidebar', 'button', 'icon',
  'feature', 'section', 'layout', 'designed', 'purpose',
];

const PHRASE_WEIGHT = 3;
const WORD_WEIGHT = 1;

/**
 * Score a normalized text against a keyword set.
 */
function score(text, phrases, words) {
  let total = 0;
  for (const phrase of phrases) {
    if (text.includes(phrase.toLowerCase())) total += PHRASE_WEIGHT;
  }
  // Split into words for single-keyword matching
  const tokens = text.split(/\s+/);
  for (const word of words) {
    if (tokens.includes(word.toLowerCase())) total += WORD_WEIGHT;
  }
  return total;
}

/**
 * Detect whether a user message should route to agent or KB.
 *
 * @param {string} text - The user's message
 * @param {string|null} currentMode - Current session mode: 'agent', 'kb', or null (auto-detect)
 * @returns {'agent'|'kb'} - The detected intent
 */
export function detectIntent(text, currentMode) {
  // Session stickiness: once in agent or kb mode, stay there
  if (currentMode === 'agent') return 'agent';
  if (currentMode === 'kb') return 'kb';

  // Auto-detect from message content
  const normalized = text.toLowerCase().replace(/['']/g, "'").replace(/[^\w\s'-]/g, ' ');

  const agentScore = score(normalized, AGENT_PHRASES, AGENT_WORDS);
  const kbScore = score(normalized, KB_PHRASES, KB_WORDS);

  // Agent wins ties — it's strictly more capable
  return kbScore > agentScore ? 'kb' : 'agent';
}
